import { t } from '@lingui/core/macro';
import { UserGroupType } from '@navio/nsdk';
import type { UserSessionData } from 'noddi-web';
import { useAuthContext, useBookingActions, useBookingProposalCustomer } from 'noddi-web';
import { useStationaryBookingActions } from '../stores/StationaryBookingStore';
import { getSelectableUserGroups, type SelectableUserGroup } from '../utils/userGroupContext';

/** Profile type label for personal / group / organization contexts. */
export const getUserGroupProfileTypeLabel = (
  userGroup: Pick<SelectableUserGroup, 'isPersonal' | 'isOrganization' | 'type'>
) => {
  if (userGroup.isPersonal || userGroup.type === UserGroupType.PERSONAL) {
    return t`Personal`;
  }
  if (userGroup.isOrganization || userGroup.type === UserGroupType.ORGANIZATION) {
    return t`Organization`;
  }
  if (userGroup.type === UserGroupType.GROUP) {
    return t`Group`;
  }
  return t`Group`;
};

type SelectUserGroupOptions = {
  /**
   * Keep the in-flight booking drafts. For a mid-flow selection (login on a
   * summary step, switching to the group that owns the car) the draft predates
   * any group context, so clearing it wipes the reservation the user just made.
   */
  keepBookingDrafts?: boolean;
};

/** A session that acts as `userGroupId`. */
const withSelectedUserGroup = (userData: UserSessionData, userGroupId: number): UserSessionData => ({
  ...userData,
  user: {
    ...userData.user,
    userGroups: userData.user.userGroups.map((userGroup) => ({
      ...userGroup,
      isSelected: userGroup.id === userGroupId
    }))
  }
});

export const useUserGroupSelection = () => {
  const { userData, updateUserData, updateImpersonatedData, impersonatedUser, currentUserGroupId } = useAuthContext();
  const { clearStore: clearMobileBookingStore } = useBookingActions();
  const proposalCustomer = useBookingProposalCustomer();
  const { clearStore: clearStationaryBookingStore } = useStationaryBookingActions();

  const selectedUserGroup = userData?.user.userGroups.find((userGroup) => userGroup.isSelected);

  const selectUserGroup = (userGroupId: number, options?: SelectUserGroupOptions) => {
    // The session id is what every group-scoped query uses, and a fresh login seeds it
    // from the default membership — so an already-flagged group still has to be written
    // through when the session is acting as another one.
    if (!userData || (selectedUserGroup?.id === userGroupId && currentUserGroupId === userGroupId)) {
      return;
    }
    // A booking made on an organization the user is not a member of (allowed by
    // some service organizations) names a group the session cannot act as.
    if (!userData.user.userGroups.some((userGroup) => userGroup.id === userGroupId)) {
      return;
    }

    const newUserData = withSelectedUserGroup(userData, userGroupId);

    if (impersonatedUser) {
      updateImpersonatedData(newUserData);
    } else {
      updateUserData(newUserData);
    }

    // Booking drafts are scoped to the selected user group — drop them on switch,
    // unless the caller is picking a group for a draft that is already in flight.
    if (!options?.keepBookingDrafts) {
      if (!proposalCustomer) {
        clearMobileBookingStore();
      }
      clearStationaryBookingStore();
    }
  };

  const selectableUserGroups = userData ? getSelectableUserGroups(userData) : [];
  const hasMultipleSelectableUserGroups = selectableUserGroups.length > 1;

  return {
    selectUserGroup,
    selectableUserGroups,
    hasMultipleSelectableUserGroups,
    selectedUserGroup,
    userData
  };
};
